import { ProgCss } from "./ProgCss.Generated";
import { AddPjaxUnmountListener } from "./Pjax";
import { createRoot } from 'react-dom/client';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import Select, { CSSObjectWithLabel, DropdownIndicatorProps, FormatOptionLabelMeta, MultiValueRemoveProps, SelectInstance, components } from 'react-select';
import { AssertNotNull, cast } from "./tools";
import { SemanticSymbol } from "./SemanticSymbols.Generated";
import { vertaal } from "./vertaal";
import { delayedFocus } from "./collectFuzzyFocusSelector";
import { matchSorter, MatchSorterOptions } from "match-sorter";
import { getTooltipTargetFromWrapper } from "./tooltips";
import parse, { HTMLReactParserOptions } from "html-react-parser";

type ProgSelectHostOptions = {
    hostEl: Element,
    options: ProgSelectItem[],
    formControlName: string,
    autoSubmit: boolean,
    isMulti: boolean,
    isDisabled: boolean,
    placeholder?: string
};

type ProgSelectProps = {
    isMulti: boolean,
    isDisabled: boolean,
    placeholder?: string,
    options: ProgSelectItem[],
    onChange: (selectedOptions: ProgSelectItem[]) => void,
    onMenuClose: (() => void) | undefined,
    onMenuOpen: (() => void) | undefined,
};

type ProgSelectItem = {
    IsReadOnly: boolean,
    HasError: boolean,
    IsSelectedInitially: boolean,
    Value: string,
    TextValue: string,
    Html?: string,
};

function createHiddenInput(name: string, value: string): HTMLInputElement {
    var hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = name;
    hiddenInput.value = value;
    return hiddenInput;
}

const DropdownIndicator = (props: DropdownIndicatorProps<ProgSelectItem>) => (
    <components.DropdownIndicator {...props}>
        <span dangerouslySetInnerHTML={{ __html: SemanticSymbol.PopoverDropdown.AsHtmlString() }}></span>
    </components.DropdownIndicator>
);

const MultiValueRemove = (props: MultiValueRemoveProps<ProgSelectItem>) => {
    // Don't show the remove (close) button for read-only items
    if (props.data.IsReadOnly) {
        return null;
    }

    return (
        <components.MultiValueRemove {...props}>
            <span dangerouslySetInnerHTML={{ __html: SemanticSymbol.Close.AsHtmlString() }}></span>
        </components.MultiValueRemove>
    );
};

const matchSorterConfig: MatchSorterOptions<ProgSelectItem> = {
    keys: [{ key: (o) => o.TextValue }],
    threshold: matchSorter.rankings.MATCHES,
    baseSort: (a, b) => (a.index < b.index ? -1 : 1), //maintain original order as tie-break, not alphabetic
};

function highlightHtmlCached(html: string, query: string, cache: Map<string, ReactNode>) {
    const key = html + "|" + query;
    const cached = cache.get(key);
    if (cached) {
        return cached;
    }
    
    const res = highlightHtml(html, query);
    cache.set(key, res);
    return res;
}

function highlightHtml(html: string, query: string): React.ReactNode {
    if (!query) {
        return parse(html);
    }

    const queryLowerCased = query.toLowerCase();

    const options: HTMLReactParserOptions = {
        replace(node) {
            if (node.type !== "text") {
                return;
            }

            const text = node.data;

            // Highlight full substring match first
            const sliceIdx = text.toLowerCase().indexOf(queryLowerCased);
            if (sliceIdx !== -1) {
                return (
                    <span>
                        {text.slice(0, sliceIdx)}
                        <strong key="slice">
                            {text.slice(sliceIdx, sliceIdx + query.length)}
                        </strong>
                        {text.slice(sliceIdx + query.length)}
                    </span>
                );
            }

            // Fallback: highlight each character in order if not found as substring
            let queryIdx = 0;
            let anyMatch = false;

            const out = text.split('').map((char, idx) => {
                const isMatch = queryIdx < queryLowerCased.length && char.toLowerCase() === queryLowerCased[queryIdx];

                if (isMatch) {
                    queryIdx++;
                    anyMatch = true;
                    return <strong key={idx}>{char}</strong>;
                }

                return char;
            });

            if (!anyMatch) {
                return;
            }

            return <span>{out}</span>;
        },
    };

    return parse(html, options);
}

function ProgSelect(ProgSelectProps: ProgSelectProps) {
    const highlightsCache = useRef(new Map<string, ReactNode>()).current;
    const [options, setOptions] = useState(ProgSelectProps.options);
    const [open, setOpen] = useState(false);
    const originalOptions = ProgSelectProps.options;
    const selectRef = useRef<SelectInstance<ProgSelectItem, boolean>>(null);

    const initiallySelected = originalOptions.filter(o => o.IsSelectedInitially);
    const [isInErrorState, setIsInErrorState] = useState<boolean>(initiallySelected.filter(o => o.HasError).length > 0);
    if (!ProgSelectProps.isMulti && initiallySelected.length > 1) {
        throw Error("Expecting only 1 selected option for single select dropdown");
    }

    const formatOptionLabel = useCallback(
        (
            option: ProgSelectItem,
            { inputValue }: FormatOptionLabelMeta<ProgSelectItem>,
        ) => highlightHtmlCached(
                option.Html ?? option.TextValue,
                inputValue,
                highlightsCache,
            ),
        [],
    );

    useEffect(() => delayedFocus.checkForEventualFocus(), []);

    return (
        <Select
            ref={selectRef}
            menuIsOpen={open}
            menuPosition="fixed"
            defaultValue={initiallySelected}
            isClearable={false}
            isMulti={ProgSelectProps.isMulti}
            isDisabled={ProgSelectProps.isDisabled}
            options={options}
            isOptionDisabled={(o) => o.IsReadOnly}
            isSearchable={true}
            noOptionsMessage={() => vertaal("Geen opties", "No options")}
            formatOptionLabel={formatOptionLabel}
            getOptionValue={(option: ProgSelectItem) => option.Value}
            filterOption={() => true}
            onKeyDown={evt => {
                if (!selectRef.current) {
                    return;
                }

                if (evt.key === "Home") {
                    evt.preventDefault();
                    selectRef.current.inputRef?.setSelectionRange(
                        0, 
                        evt.shiftKey 
                            ? selectRef.current.inputRef?.selectionEnd 
                            : 0, 
                        selectRef.current.inputRef?.selectionDirection ?? undefined
                    );
                }
                else if (evt.key === "End") {
                    evt.preventDefault();
                    selectRef.current.inputRef?.setSelectionRange(
                        evt.shiftKey 
                            ? selectRef.current.inputRef?.selectionStart
                            : selectRef.current.inputRef?.value.length,
                        selectRef.current.inputRef?.value.length, 
                        selectRef.current.inputRef?.selectionDirection ?? undefined
                    );
                }
                else if (evt.key === "Enter" || evt.key === "Tab") {
                    if (evt.key === "Enter") {
                        setOpen(!open);
                        evt.preventDefault();
                    }
                    else if (evt.key === "Tab" && open) {
                        setOpen(false);
                    }

                    if (open && selectRef.current.state.focusedOption) {
                        selectRef.current.selectOption(selectRef.current.state.focusedOption);
                    }
                }
            }}
            onInputChange={(search) => {
                let toFilter = originalOptions;
                if (selectRef.current?.state.focusedOption) {
                    const oldIdx = toFilter.indexOf(selectRef.current.state.focusedOption);
                    if (oldIdx > 0) {
                        //give the old focused item the best chances of retaining focus...
                        toFilter = [toFilter[oldIdx], ...toFilter.slice(0, oldIdx), ...toFilter.slice(oldIdx+1, toFilter.length)];
                    }
                }
                const filtered = search
                    ? matchSorter(originalOptions, search, matchSorterConfig)
                    : originalOptions;
                setOptions(filtered);

                if (filtered[0] && selectRef.current && selectRef.current.state.focusedOption) {
                    //...but if there's truly a new best match, then do focus that instead.
                    selectRef.current.state.focusedOption = filtered[0];
                }
            }}
            onChange={(e) => {
                const selectedOptions = [e].flat().filter(x => x != null);
                setIsInErrorState(selectedOptions.some(o => o.HasError));
                ProgSelectProps.onChange(selectedOptions);
            }}
            onMenuOpen={() => {
                setOpen(true);
                if (ProgSelectProps.onMenuOpen) {
                    ProgSelectProps.onMenuOpen();
                }
            }}
            onMenuClose={() => {
                setOpen(false);
                if (ProgSelectProps.onMenuClose) {
                    ProgSelectProps.onMenuClose();
                }
            }}
            closeMenuOnSelect={!ProgSelectProps.isMulti}
            placeholder={ProgSelectProps.placeholder}
            menuPlacement="auto"
            components={{
                IndicatorSeparator: () => null,
                DropdownIndicator,
                MultiValueRemove
            }}
            styles={{
                control: (base: CSSObjectWithLabel) => ({
                    ...base,
                }),
            }}
            classNames={{
                control: (props) => [
                    ProgCss.ProgSelect_Control,
                    props.menuIsOpen ? ProgCss.is_open : undefined,
                    isInErrorState ? ProgCss.has_error : undefined,
                ].join(' '),
                input: () => [
                    ProgCss.ProgSelect_Input,
                ].join(' '),
                singleValue: (props) => [
                    ProgCss.ProgSelect_SingleValue,
                    props.data.HasError ? ProgCss.has_error : undefined,
                    props.data.IsReadOnly ? ProgCss.is_disabled : undefined,
                ].join(' '),
                multiValue: () => [
                    ProgCss.ProgSelect_MultiValue,
                ].join(' '),
                dropdownIndicator: () => [
                    ProgCss.ProgSelect_DropdownIndicator,
                ].join(' '),
                menu: () => [
                    ProgCss.ProgSelect_Menu,
                    isInErrorState ? ProgCss.has_error : undefined,
                ].join(' '),
                menuList: () => [
                    ProgCss.ProgSelect_MenuList,
                ].join(' '),
                option: (props) => [
                    ProgCss.ProgSelect_Option,
                    props.data.HasError ? ProgCss.has_error : undefined,
                    props.data.IsReadOnly ? ProgCss.is_disabled : undefined,
                    props.isSelected ? ProgCss.is_selected : undefined,
                    props.isFocused ? ProgCss.is_focused : undefined,
                ].join(' '),
                valueContainer: () => [
                    ProgCss.ProgSelect_ValueContainer,
                ].join(' ')
            }}
        />
    )
}

export function StartProgSelect(scopeEl: HTMLElement) {
    flushSync(() => {//Maak de react-update sync ivm paint flashing
        for (const el of scopeEl.getElementsByClassName(ProgCss.ProgSelect_Wrapper)) {
            if (el instanceof HTMLSelectElement) {
                const selectEl = el;
                const options = [...selectEl.options].map(opt => ({
                    IsReadOnly: false,
                    HasError: false,
                    IsSelectedInitially: opt.value != '' && opt.selected, // native selects hebben de 1e waarde altijd voor-geselecteerd en we willen een placeholder kunnen tonen
                    Value: opt.value,
                    TextValue: opt.text,
                } as ProgSelectItem));
                const autoSubmit = selectEl.hasAttribute("data-autosubmit");
                const scrollIntoview = selectEl.hasAttribute("data-scroll-into-view");
                const formControlName = selectEl.name;
                const isMulti = selectEl.multiple;
                const isDisabled = selectEl.disabled;

                const hostEl = document.createElement("div");
                hostEl.classList.add(ProgCss.ProgSelect_Wrapper);
                if (scrollIntoview) {
                    hostEl.classList.add(ProgCss.js_ScrollIntoViewElement);
                }
                hostEl.style.width = selectEl.getBoundingClientRect().width + "px";
                hostEl.setAttribute("data-modulename", formControlName);
                hostEl.setAttribute("data-delayed-focusable", "");
                selectEl.replaceWith(hostEl);
                const placeholder = el.getAttribute("data-placeholder") || undefined;
                createProgSelect({ hostEl, options, formControlName, autoSubmit, isMulti, isDisabled, placeholder });
            } else {
                const options = Array.from<ProgSelectItem>(JSON.parse(AssertNotNull(el.getAttribute("data-options"))));
                const formControlName = AssertNotNull(el.getAttribute("data-callback"));
                const isMulti = el.hasAttribute("data-is-multi");
                const isDisabled = el.hasAttribute("data-is-disabled");
                const autoSubmit = el.hasAttribute("data-autosubmit");
                const placeholder = el.getAttribute("data-placeholder") || undefined;
                createProgSelect({ hostEl: el, options, formControlName, autoSubmit, isMulti, isDisabled, placeholder });
            }
        }
    });
}


function createProgSelect({ hostEl, options, formControlName, autoSubmit, isMulti, isDisabled, placeholder }: ProgSelectHostOptions) {
    // Browser translation replaces React-managed text nodes, which crashes React on the next update.
    hostEl.setAttribute("translate", "no");
    const reactRoot = createRoot(hostEl);
    const container = document.createElement("span");
    container.hidden = true;
    const callbacksContainer = AssertNotNull(hostEl.parentElement).appendChild(container);
    const initiallySelected = options.filter(o => o.IsSelectedInitially);

    let menuIsOpenened = false;
    let currentlySelected = initiallySelected;

    const checkForChanges = () => {
        if (!menuIsOpenened) {
            hostEl.setAttribute('data-smoketest-text', JSON.stringify(currentlySelected.map(o => o.TextValue)));
            callbacksContainer.replaceChildren(...currentlySelected.map(selectedItem => createHiddenInput(formControlName, selectedItem.Value)));

            if (autoSubmit && JSON.stringify(initiallySelected.map(o => o.Value)) !== JSON.stringify(currentlySelected.map(o => o.Value))) {
                document.forms[0].requestSubmit();
            }
        }
    };

    const removeOrphanedDropdowns = () => {
        // Items are removed from the menu once selected so its origin (wrapper) no longer exists and they remain always opened
        // Tooltips are normally removed on "mouseleave" so we do this instead

        const menuEl = hostEl.querySelector(`.${ProgCss.ProgSelect_Menu}`);
        if (menuEl) {
            const openTooltips = [...document.querySelectorAll(`.${ProgCss.Tooltip}`)];
            for (const tooltipElUntyped of openTooltips) {
                const tooltipEl = cast(tooltipElUntyped, HTMLElement);
                const targetEl = getTooltipTargetFromWrapper(cast(tooltipEl, HTMLElement));
                if (!targetEl || !menuEl.contains(targetEl)) continue;
                tooltipEl.remove();
            }
        }
    };

    checkForChanges(); // never autosubmits because there aren't any changes.
    AddPjaxUnmountListener(() => reactRoot.unmount());
    reactRoot.render(
        <ProgSelect
            isMulti={isMulti}
            isDisabled={isDisabled}
            placeholder={placeholder}
            options={options}
            onChange={selectedOptions => { currentlySelected = selectedOptions; checkForChanges(); removeOrphanedDropdowns(); }}
            onMenuOpen={() => { menuIsOpenened = true; checkForChanges(); }}
            onMenuClose={() => { menuIsOpenened = false; checkForChanges(); removeOrphanedDropdowns(); }}
        />
    );
}
